import { useEffect, useMemo, useRef, useState } from 'react';
import Header from '../components/Header';
import VersionBanner from '../components/VersionBanner';
import FranchiseeDetail from './FranchiseeDetail';
import NetworkView from './NetworkView';
import { useFranchiseesQuery } from '../api/queries';
import { useAuth } from '../hooks/useAuth';
import type { ContextView, TabId } from '../types';

type UrlState = {
  af: string | null;
  fbc: string | null;
  tab: TabId | null;
};

function readUrlState(): UrlState {
  const sp = new URLSearchParams(window.location.search);
  const af = sp.get('af');
  const fbc = sp.get('fbc');
  const tab = sp.get('tab') as TabId | null;
  return {
    af: af && af.length ? af : null,
    fbc: fbc && fbc.length ? fbc : null,
    tab: tab && tab.length ? tab : null,
  };
}

function writeUrlState(view: ContextView, tab: TabId | null, replace: boolean) {
  const sp = new URLSearchParams();
  if (view.kind === 'franchisee') {
    sp.set('af', view.afNumber);
    if (tab) sp.set('tab', tab);
  } else if (view.kind === 'fbc') {
    sp.set('fbc', view.fbcName);
  }
  const qs = sp.toString();
  const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) return; // no-op (e.g. after popstate the URL is already correct)
  if (replace) {
    window.history.replaceState(null, '', next);
  } else {
    window.history.pushState(null, '', next);
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const initial = readUrlState();

  const initialView: ContextView = useMemo(() => {
    if (initial.af) return { kind: 'franchisee', afNumber: initial.af };
    if (initial.fbc) return { kind: 'fbc', fbcName: initial.fbc };
    if (!user) return { kind: 'network' };
    if (user.role === 'admin') return { kind: 'network' };
    if (user.role === 'fbc') return { kind: 'fbc', fbcName: user.name ?? '' };
    // franchisee
    if (user.afNumbers && user.afNumbers.length === 1) {
      return { kind: 'franchisee', afNumber: user.afNumbers[0] };
    }
    if (user.afNumbers && user.afNumbers.length > 1) {
      return { kind: 'franchisee', afNumber: user.afNumbers[0] };
    }
    return { kind: 'network' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [view, setView] = useState<ContextView>(initialView);
  const [tab, setTab] = useState<TabId | null>(initial.tab);

  // URL-sync mode for the next setView. Initial render and silent rewrites
  // (popstate echo, satellite→parent redirect) should not add history entries.
  const isFirstRender = useRef(true);
  const replaceNext = useRef(false);

  // If we just learned the user's role, reset the view to its role default.
  useEffect(() => {
    if (!user) return;
    if (initial.af || initial.fbc) return;
    setView(initialView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // If the view points at a satellite AF that shares an office with its
  // parent, transparently swap to the parent — one office, one dashboard.
  const { data: franchisees } = useFranchiseesQuery();
  useEffect(() => {
    if (view.kind !== 'franchisee') return;
    if (!franchisees) return;
    const f = franchisees.find((x) => x.af_number === view.afNumber);
    if (!f) return;
    if (f.has_own_office === false && f.parent_af) {
      replaceNext.current = true; // transparent redirect — don't pollute history
      setView({ kind: 'franchisee', afNumber: f.parent_af });
    }
  }, [view, franchisees]);

  // Sync URL to view. User-initiated changes push; the first render and
  // silent rewrites replace.
  useEffect(() => {
    const replace = isFirstRender.current || replaceNext.current;
    isFirstRender.current = false;
    replaceNext.current = false;
    writeUrlState(view, view.kind === 'franchisee' ? tab : null, replace);
  }, [view, tab]);

  // Browser back/forward: re-sync view+tab from the URL the browser just
  // restored. The URL is already correct, so flag the next URL-sync as a
  // replace (no-op via the early-return in writeUrlState).
  useEffect(() => {
    function onPop() {
      const s = readUrlState();
      replaceNext.current = true;
      if (s.af) {
        setView({ kind: 'franchisee', afNumber: s.af });
        setTab(s.tab ?? 'tab-website');
      } else if (s.fbc) {
        setView({ kind: 'fbc', fbcName: s.fbc });
        setTab(null);
      } else {
        setView({ kind: 'network' });
        setTab(null);
      }
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function handleChangeView(next: ContextView) {
    setView(next);
    if (next.kind !== 'franchisee') setTab(null);
  }

  function handleDrillFranchisee(af: string) {
    setView({ kind: 'franchisee', afNumber: af });
    setTab('tab-website');
  }

  function handleDrillFbc(fbcName: string) {
    setView({ kind: 'fbc', fbcName });
    setTab(null);
  }

  // Role-based "home" used by the in-app back link on detail views.
  // Returns null when the user has no parent context (e.g. franchisee role
  // viewing their own AF — they're already home).
  const homeTarget: { view: ContextView; label: string } | null = (() => {
    if (!user) return null;
    if (user.role === 'admin') {
      return { view: { kind: 'network' }, label: 'Network Overview' };
    }
    if (user.role === 'fbc') {
      return { view: { kind: 'fbc', fbcName: user.name ?? '' }, label: 'My Portfolio' };
    }
    return null;
  })();

  function handleBackHome() {
    if (!homeTarget) return;
    setView(homeTarget.view);
    setTab(null);
  }

  return (
    <div className="app-shell">
      <VersionBanner />
      <Header view={view} onChangeView={handleChangeView} />
      {view.kind === 'network' && (
        <main className="tab-content">
          <NetworkView
            onDrillFranchisee={handleDrillFranchisee}
            onDrillFbc={user?.role === 'admin' ? handleDrillFbc : undefined}
          />
        </main>
      )}
      {view.kind === 'fbc' && (
        <main className="tab-content">
          <NetworkView
            fbcName={view.fbcName}
            title={user?.role === 'fbc' ? 'My Portfolio' : `${view.fbcName}'s Portfolio`}
            subtitle={
              user?.role === 'fbc'
                ? 'Performance across your assigned franchisees'
                : `Franchisees coached by ${view.fbcName}`
            }
            onDrillFranchisee={handleDrillFranchisee}
            onBackHome={user?.role === 'admin' ? handleBackHome : undefined}
            backHomeLabel={user?.role === 'admin' ? 'Network Overview' : undefined}
          />
        </main>
      )}
      {view.kind === 'franchisee' && (
        <FranchiseeDetail
          af={view.afNumber}
          initialTab={tab ?? 'tab-website'}
          onChangeTab={setTab}
          onBackHome={homeTarget ? handleBackHome : undefined}
          backHomeLabel={homeTarget?.label}
        />
      )}
    </div>
  );
}
